import React from "react";
import { Newspaper, TrendingUp, Users, Globe, ExternalLink } from "lucide-react";

const getSentimentColor = (s) =>
  s === "positive" ? "text-emerald-400 bg-emerald-500/20" :
  s === "negative" ? "text-red-400 bg-red-500/20" :
  "text-slate-400 bg-slate-500/20";

export const ReportNews = ({ reviewsData, strategy, brandInput }) => {
  const newsData = reviewsData?.news_data || {};
  const companyNews = newsData.company_mentions || [];
  const competitorNews = newsData.competitor_mentions || [];
  const insights = newsData.insights || {};
  const prAngles = strategy?.marketing_strategy?.press_release_angles || [];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center">
          <Newspaper className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white">{insights.company_mentions || 0}</p>
          <p className="text-slate-400 text-sm">Your Mentions</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center">
          <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white">{insights.competitor_mentions || 0}</p>
          <p className="text-slate-400 text-sm">Competitor Mentions</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center">
          <Globe className="w-8 h-8 text-purple-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white">{insights.total_sources || 0}</p>
          <p className="text-slate-400 text-sm">Unique Sources</p>
        </div>
      </div>

      {insights.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-2xl p-6">
          <h3 className="font-semibold text-emerald-400 mb-4">PR Recommendations</h3>
          <ul className="space-y-2">
            {insights.recommendations.map((rec) => (
              <li key={`rec-${rec.slice(0, 30)}`} className="flex items-start gap-2 text-slate-300">
                <TrendingUp className="w-4 h-4 mt-1 text-emerald-400 flex-shrink-0" /> {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {companyNews.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4">
            News Coverage for {brandInput?.company_name || "Your Business"}
          </h3>
          <div className="space-y-3">
            {companyNews.map((article, i) => (
              <div key={`news-${article.title?.slice(0, 30)}-${i}`} className="p-4 bg-slate-800/50 rounded-xl flex items-start gap-4">
                <div className="flex-1">
                  <a href={article.link} target="_blank" rel="noopener noreferrer"
                    className="font-medium text-white hover:text-emerald-400 flex items-start gap-2 transition-colors">
                    {article.title}
                    <ExternalLink className="w-4 h-4 mt-1 flex-shrink-0 text-slate-500" />
                  </a>
                  <p className="text-slate-400 text-sm mt-1">{article.source} {article.date && `· ${article.date}`}</p>
                  {article.snippet && <p className="text-slate-500 text-sm mt-2">{article.snippet}</p>}
                </div>
                {article.sentiment && (
                  <span className={`text-xs px-2 py-1 rounded-full ${getSentimentColor(article.sentiment)}`}>{article.sentiment}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {competitorNews.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4">Competitor News Activity</h3>
          <div className="space-y-4">
            {competitorNews.map((comp) => (
              <div key={`compnews-${comp.competitor}`}>
                <h4 className="font-medium text-slate-300 mb-2">{comp.competitor} ({comp.articles?.length || 0} articles)</h4>
                <div className="space-y-2 pl-4 border-l-2 border-slate-800">
                  {(comp.articles || []).slice(0, 3).map((article, j) => (
                    <a key={`compart-${article.title?.slice(0, 20)}-${j}`} href={article.link} target="_blank" rel="noopener noreferrer"
                      className="block text-sm text-slate-400 hover:text-emerald-400 transition-colors">
                      {article.title}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {prAngles.length > 0 && (
        <div className="bg-gradient-to-br from-purple-900/30 to-slate-900 border border-purple-500/30 rounded-2xl p-6">
          <h3 className="font-semibold text-purple-400 mb-4 flex items-center gap-2">
            <Newspaper className="w-5 h-5" /> Press Release Angles
          </h3>
          <div className="space-y-4">
            {prAngles.map((pr, i) => (
              <div key={`pr-${pr.angle || pr.title || i}`} className="p-4 bg-slate-800/50 rounded-xl">
                <h4 className="font-semibold text-white mb-1">{pr.angle || pr.title}</h4>
                <p className="text-slate-300 text-sm mb-2">{pr.hook || pr.description}</p>
                {pr.target_outlets && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pr.target_outlets.map((outlet) => (
                      <span key={`outlet-${outlet}`} className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">{outlet}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {companyNews.length === 0 && competitorNews.length === 0 && prAngles.length === 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 text-center">
          <Newspaper className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No news mentions found. Consider creating a PR strategy to increase media visibility.</p>
        </div>
      )}
    </div>
  );
};
